import { IonButton, IonIcon, IonSpinner, IonText } from '@ionic/react';
import {
  chevronDownOutline,
  chevronUpOutline,
  folderOpenOutline,
  musicalNotesOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { on, removeListener } from '../../helper/electron'; // Adjust path if needed
import { RootState } from '../../store/store';
import './ScanningIndicator.scss';

// Define expected progress event data structures
interface RescanFoldersProgress {
  total: number;
  numOfProcessed: number;
  folder: { path: string; id: string };
  track?: {
    filePath: string;
    tags?: {
      title?: string;
      artist?: string[];
    };
  };
  phase?: 'processing' | 'complete';
  filesPerSecond?: string;
  estimatedTimeRemaining?: string;
}

interface AddFolderProgress {
  total: number;
  numOfProcessed: number;
  folder: { path: string; id: string };
  track?: {
    filePath: string;
    tags?: {
      title?: string;
      artist?: string[];
    };
  };
  phase?: 'processing' | 'complete';
  filesPerSecond?: string;
  estimatedTimeRemaining?: string;
}

const TRANSPARENT_DARK_BG = 'rgba(20, 20, 20, 0.95)'; // Define background color constant
const PRIMARY_COLOR = 'var(--ion-color-primary)'; // Use CSS variable for primary color

export const ScanningIndicator: React.FC = () => {
  const isScanning = useSelector(
    (state: RootState) => state.scanningState.isScanning,
  );
  // Remove the old progress value from Redux state if no longer used
  // const progressPercentage = useSelector((state: RootState) => state.scanningState.progress);

  const [progressMessage, setProgressMessage] =
    useState<string>('Preparing scan...');
  const [progressValue, setProgressValue] = useState<number>(0); // Add state for progress value
  const [currentFolder, setCurrentFolder] = useState<string>('');
  const [currentTrack, setCurrentTrack] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] =
    useState<string>('');
  const [filesPerSecond, setFilesPerSecond] = useState<string>('');
  const [startTime, setStartTime] = useState<number | null>(null);
  const [operationType, setOperationType] = useState<'scanning' | 'adding'>(
    'scanning',
  );

  // Track total files across all operations for better estimates
  const [totalFilesAcrossAll, setTotalFilesAcrossAll] = useState<number>(0);
  const [processedFilesAcrossAll, setProcessedFilesAcrossAll] =
    useState<number>(0);

  // Calculate estimated time remaining
  const calculateTimeRemaining = (
    processed: number,
    total: number,
    startTimeMs: number,
  ): string => {
    if (processed === 0 || !startTimeMs) return '';

    const elapsed = Date.now() - startTimeMs;
    const rate = processed / elapsed; // files per millisecond
    const remaining = total - processed;
    const estimatedMs = remaining / rate;

    if (estimatedMs < 60000) {
      // Less than 1 minute
      return `~${Math.round(estimatedMs / 1000)}s remaining`;
    } else if (estimatedMs < 3600000) {
      // Less than 1 hour
      return `~${Math.round(estimatedMs / 60000)}m remaining`;
    } else {
      const hours = Math.floor(estimatedMs / 3600000);
      const minutes = Math.round((estimatedMs % 3600000) / 60000);
      return `~${hours}h ${minutes}m remaining`;
    }
  };

  useEffect(() => {
    if (!isScanning) {
      // Reset all state when scanning stops
      setProgressMessage('Preparing scan...');
      setProgressValue(0);
      setCurrentFolder('');
      setCurrentTrack('');
      setEstimatedTimeRemaining('');
      setFilesPerSecond('');
      setStartTime(null);
      setTotalFilesAcrossAll(0);
      setProcessedFilesAcrossAll(0);
      return;
    }

    // Set start time when scanning begins
    if (!startTime) {
      setStartTime(Date.now());
    }

    // Listener for main library rescan progress
    const handleRescanProgress = (progress: RescanFoldersProgress) => {
      if (
        !progress ||
        typeof progress.numOfProcessed !== 'number' ||
        typeof progress.total !== 'number'
      ) {
        return;
      }

      setOperationType('scanning');

      // Update folder info
      if (progress.folder?.path) {
        const folderName =
          progress.folder.path.split('/').pop() || progress.folder.path;
        setCurrentFolder(folderName);
      }

      // Update current track info
      if (progress.track?.filePath) {
        const fileName =
          progress.track.filePath.split('/').pop() || progress.track.filePath;
        let trackInfo = fileName;

        if (progress.track.tags?.title && progress.track.tags?.artist?.length) {
          trackInfo = `${progress.track.tags.artist[0]} - ${progress.track.tags.title}`;
        }

        setCurrentTrack(trackInfo);
      }

      // Update progress
      const newProcessed = progress.numOfProcessed;
      const newTotal = progress.total;

      setProcessedFilesAcrossAll(newProcessed);
      setTotalFilesAcrossAll(newTotal);
      setProgressValue(newTotal > 0 ? newProcessed / newTotal : 0);

      // Update progress message
      setProgressMessage(
        `Scanning: ${newProcessed} of ${newTotal} tracks processed`,
      );

      // Use backend-provided time estimate if available, otherwise calculate
      if (progress.estimatedTimeRemaining) {
        setEstimatedTimeRemaining(progress.estimatedTimeRemaining);
      } else if (startTime && newTotal > 0) {
        const timeRemaining = calculateTimeRemaining(
          newProcessed,
          newTotal,
          startTime,
        );
        setEstimatedTimeRemaining(timeRemaining);
      }

      // Update files per second if provided
      if (progress.filesPerSecond) {
        setFilesPerSecond(progress.filesPerSecond);
      }
    };

    // Listener for folder addition progress (my-music/add-folder events)
    const handleAddFolderProgress = (progress: AddFolderProgress) => {
      if (
        !progress ||
        typeof progress.numOfProcessed !== 'number' ||
        typeof progress.total !== 'number'
      ) {
        return;
      }

      setOperationType('adding');

      // Update folder info
      if (progress.folder?.path) {
        const folderName =
          progress.folder.path.split('/').pop() || progress.folder.path;
        setCurrentFolder(folderName);
      }

      // Update current track info
      if (progress.track?.filePath) {
        const fileName =
          progress.track.filePath.split('/').pop() || progress.track.filePath;
        let trackInfo = fileName;

        if (progress.track.tags?.title && progress.track.tags?.artist?.length) {
          trackInfo = `${progress.track.tags.artist[0]} - ${progress.track.tags.title}`;
        }

        setCurrentTrack(trackInfo);
      }

      // Update progress
      const newProcessed = progress.numOfProcessed;
      const newTotal = progress.total;

      setProcessedFilesAcrossAll(newProcessed);
      setTotalFilesAcrossAll(newTotal);
      setProgressValue(newTotal > 0 ? newProcessed / newTotal : 0);

      // Update progress message
      setProgressMessage(
        `Adding folder: ${newProcessed} of ${newTotal} tracks processed`,
      );

      // Use backend-provided time estimate if available, otherwise calculate
      if (progress.estimatedTimeRemaining) {
        setEstimatedTimeRemaining(progress.estimatedTimeRemaining);
      } else if (startTime && newTotal > 0) {
        const timeRemaining = calculateTimeRemaining(
          newProcessed,
          newTotal,
          startTime,
        );
        setEstimatedTimeRemaining(timeRemaining);
      }

      // Update files per second if provided
      if (progress.filesPerSecond) {
        setFilesPerSecond(progress.filesPerSecond);
      }
    };

    // Listener for overall completion
    const handleScanComplete = () => {
      setProgressMessage('Scan complete!');
      setProgressValue(1);
      setEstimatedTimeRemaining('Complete!');

      // Auto-collapse after completion
      setTimeout(() => {
        setIsExpanded(false);
      }, 3000);
    };

    console.log('[ScanningIndicator] Attaching enhanced listeners');
    on('my-music/rescan-folders', handleRescanProgress);
    on('my-music/add-folder', handleAddFolderProgress);
    on('my-music/scan-complete', handleScanComplete);

    // Cleanup function
    return () => {
      console.log('[ScanningIndicator] Removing enhanced listeners');
      removeListener('my-music/rescan-folders');
      removeListener('my-music/add-folder');
      removeListener('my-music/scan-complete');
    };
  }, [isScanning, startTime]);

  // Only render if scanning is active
  if (!isScanning) {
    return null;
  }

  // Calculate background gradient style based on progress
  const progressPercent = Math.round(progressValue * 100);
  const backgroundStyle = {
    background: `linear-gradient(to right, ${PRIMARY_COLOR} ${progressPercent}%, ${TRANSPARENT_DARK_BG} ${progressPercent}%)`,
  };

  const formatProcessedCount = (count: number): string => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  return (
    <div className="scanning-indicator-modern" style={backgroundStyle}>
      {/* Main progress row */}
      <div className="progress-main-row">
        <div className="progress-icon">
          {operationType === 'adding' ? (
            <IonIcon icon={folderOpenOutline} />
          ) : (
            <IonIcon icon={musicalNotesOutline} />
          )}
        </div>

        <div className="progress-info">
          <IonText className="progress-text">{progressMessage}</IonText>
          <div className="progress-details-row">
            {estimatedTimeRemaining && (
              <IonText className="time-remaining">
                {estimatedTimeRemaining}
              </IonText>
            )}
            {filesPerSecond && (
              <IonText className="files-per-second">
                {filesPerSecond} files/sec
              </IonText>
            )}
          </div>
        </div>

        <div className="progress-stats">
          <span className="progress-percentage">{progressPercent}%</span>
          <span className="progress-count">
            {formatProcessedCount(processedFilesAcrossAll)}/
            {formatProcessedCount(totalFilesAcrossAll)}
          </span>
        </div>

        <IonSpinner name="dots" className="progress-spinner" />

        <IonButton
          fill="clear"
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          className="expand-button"
        >
          <IonIcon
            icon={isExpanded ? chevronUpOutline : chevronDownOutline}
            slot="icon-only"
          />
        </IonButton>
      </div>

      {/* Expandable details */}
      {isExpanded && (
        <div className="progress-details">
          {currentFolder && (
            <div className="detail-row">
              <span className="detail-label">Current folder:</span>
              <span className="detail-value">{currentFolder}</span>
            </div>
          )}
          {currentTrack && (
            <div className="detail-row">
              <span className="detail-label">Processing:</span>
              <span className="detail-value">{currentTrack}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Operation:</span>
            <span className="detail-value">
              {operationType === 'adding'
                ? 'Adding new folder'
                : 'Scanning library'}
            </span>
          </div>
          {filesPerSecond && (
            <div className="detail-row">
              <span className="detail-label">Speed:</span>
              <span className="detail-value">{filesPerSecond} files/sec</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScanningIndicator;
